"use client"
import { Search, Plus } from "lucide-react"
import React, { useState, useRef, useEffect } from "react"

const PRIMARY = "#8C57FF"

/** Renders a single multi-select option row with a bold primary label and an
 *  optional muted secondary line (subtitle), matching LinkOptionItem in
 *  standard-input-field.tsx. */
const MultiSelectOptionItem = ({
  option,
  isSelected,
}: {
  option: { value: string; label: string; subtitle?: string }
  isSelected: boolean
}) => (
  <div className="flex flex-col min-w-0">
    {/* Primary — bold ID / code */}
    <span
      className={`text-sm font-bold leading-snug ${
        isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
      }`}
    >
      {option.label}
    </span>
    {/* Secondary — subtitle or raw value when they differ */}
    {(option.subtitle || option.value !== option.label) && (
      <span className="text-xs text-[#8C57FF] dark:text-[#A87FF5] mt-0.5 leading-snug font-normal">
        {option.subtitle ?? option.value}
      </span>
    )}
  </div>
)

interface MultiSelectLinkedFieldProps {
  label?: string
  value?: string[]
  // Legacy academics-page callers pass a "read only" boolean instead of
  // `disabled` — accept both.
  readOnly?: boolean
  // Some academics/finance forms pass an `allValue` sentinel option value
  // that toggles "select all" behavior. Accepted here so the callers compile;
  // treated as informational (the selection is still driven by `value`).
  allValue?: string
  /** Alias for `value` accepted for parent-portal ports that use `selectedValues`. */
  selectedValues?: string[]
  onChange: (value: string[]) => void
  options?: { value: string; label: string; subtitle?: string }[]
  placeholder?: string
  required?: boolean
  className?: string
  addNewLabel?: string
  /**
   * "Add new" trigger. Return value is ignored — kept loose so ported call
   * sites that return a fresh id (string | number) from the handler still
   * type-check.
   */
  onAddNew?: () => void | string | number
  /**
   * When true the field becomes read-only: the trigger no longer opens the
   * dropdown, the dim styling matches `StandardInputField` (`opacity-50` +
   * `pointer-events-none`), and toggling values is disallowed. Existing
   * selections remain visible.
   */
  disabled?: boolean
  /**
   * When true, the field is rendered inside a table cell:
   *  - the rounded outline border around the trigger is suppressed
   *  - the floating top-left label is hidden (the column header already
   *    labels the field).
   */
  cellMode?: boolean
  /**
   * When true, the dropdown uses absolute positioning relative to the field
   * instead of fixed positioning relative to the viewport. Use this when the
   * field is rendered inside a Dialog/Modal to prevent the dropdown from
   * escaping the dialog's stacking context.
   */
  insideDialog?: boolean
}

export function MultiSelectLinkedField({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = "Select",
  required = false,
  className = "",
  addNewLabel,
  onAddNew,
  disabled = false,
  cellMode = false,
  insideDialog = false,
}: MultiSelectLinkedFieldProps) {
  // Normalize value in case undefined is explicitly passed, overriding the default
  const safeValue: string[] = Array.isArray(value) ? value : []
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const filteredOptions = options.filter((option) => {
    const q = searchTerm.toLowerCase()
    return (
      (option?.label?.toLowerCase()?.includes(q) ?? false) ||
      (option?.subtitle?.toLowerCase()?.includes(q) ?? false)
    )
  })

  const handleToggleOption = (optionValue: string) => {
    if (disabled) return
    if (safeValue.includes(optionValue)) {
      onChange(safeValue.filter((v) => v !== optionValue))
    } else {
      onChange([...safeValue, optionValue])
    }
  }

  const handleSelectAll = () => {
    if (disabled) return
    onChange(options.map((o) => o.value))
  }

  const handleUnselectAll = () => {
    if (disabled) return
    onChange([])
  }

  const handleToggleDropdown = () => {
    if (disabled) return
    if (isOpen) {
      setIsOpen(false)
      setSearchTerm("")
    } else {
      if (insideDialog) {
        // Inside a Dialog: use absolute positioning so the dropdown stays in
        // the dialog's stacking context and doesn't escape to the viewport.
        setDropdownStyle({
          position: "absolute",
          top: "100%",
          left: 0,
          minWidth: "100%",
          zIndex: 50,
          marginTop: 4,
        })
      } else if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const dropdownHeight = 320
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        const flipUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
        setDropdownStyle({
          position: "fixed",
          ...(flipUp
            ? { bottom: window.innerHeight - rect.top + 4 }
            : { top: rect.bottom + 4 }),
          left: rect.left,
          minWidth: Math.max(rect.width, 220),
          zIndex: 9999,
        })
      }
      setIsOpen(true)
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const selectedLabels = safeValue
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean) as string[]

  const allSelected = options.length > 0 && options.every((o) => safeValue.includes(o.value))

  const displayText =
    safeValue.length === 0
      ? placeholder
      : allSelected
        ? "All selected"
        : selectedLabels.length === 1
          ? selectedLabels[0]
          : `${selectedLabels.length} selected`

  return (
    <div
      className={`relative ${className}${disabled ? " opacity-50 pointer-events-none select-none" : ""}`}
      ref={dropdownRef}
      aria-disabled={disabled || undefined}
    >
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleToggleDropdown}
        className={`relative w-full bg-white dark:bg-[#312D4B] cursor-pointer flex items-center gap-2 transition-colors
          ${cellMode
            ? "border-0 px-2 py-2 min-h-[40px]"
            : `px-3 py-3 border-[2.5px] rounded-md min-h-[48px] ${
                isOpen
                  ? "border-[#8C57FF]"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              }`
          }`}
      >
        {/* Floating label */}
        {!cellMode && (
          <span
            className={`absolute -top-2 left-3 px-1 text-sm font-medium pointer-events-none
              bg-white dark:bg-[#312D4B] transition-colors
              ${isOpen ? "text-[#8C57FF]" : "text-gray-700 dark:text-gray-300"}`}
          >
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </span>
        )}

        {/* Display value */}
        <span
          className={`flex-1 truncate text-sm
            ${safeValue.length === 0
              ? "text-gray-400 dark:text-gray-500"
              : "text-gray-900 dark:text-gray-100"
            }`}
        >
          {displayText}
        </span>

        {/* Count badge */}
        {safeValue.length > 0 && !allSelected && (
          <span
            className="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded"
            style={{ color: PRIMARY, background: `${PRIMARY}1a` }}
          >
            {safeValue.length}
          </span>
        )}


      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="bg-white dark:bg-[#312D4B] border rounded-md shadow-lg overflow-hidden"
          style={{ ...dropdownStyle, borderColor: `${PRIMARY}4d`, width: "max-content" }}
        >
          {/* Search field */}
          <div className="p-2.5 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
              <Search className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                placeholder={`Search ${(label ?? "options").toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100
                  placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Select All / Unselect All */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={allSelected}
              className="text-xs font-semibold px-3 py-1.5 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                color: PRIMARY,
                background: `${PRIMARY}1a`,
              }}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleUnselectAll}
              disabled={safeValue.length === 0}
              className="text-xs font-semibold px-3 py-1.5 rounded-md transition-all
                text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50
                disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Unselect All
            </button>
            {safeValue.length > 0 && (
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                {safeValue.length} / {options.length}
              </span>
            )}
          </div>

          {/* Options list */}
          <div className="max-h-52 overflow-y-auto p-2.5 space-y-1.5">
            {filteredOptions.length === 0 ? (
              <p className="py-4 text-sm text-center text-gray-400 dark:text-gray-500">
                No options found
              </p>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = safeValue.includes(option.value)
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer
                      select-none transition-all border
                      bg-[#F4F5FA] dark:bg-[#28243D]
                      ${isSelected ? "" : "hover:border-[#8C57FF]/30"}`}
                    style={{ borderColor: isSelected ? PRIMARY : "transparent" }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleOption(option.value)}
                      className="rounded shrink-0"
                      style={{ accentColor: PRIMARY }}
                    />
                    <MultiSelectOptionItem option={option} isSelected={isSelected} />
                  </label>
                )
              })
            )}
          </div>

          {/* Add new */}
          {onAddNew && (
            <div className="px-2.5 pb-2.5 pt-1 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onAddNew()
                }}
                className="flex items-center gap-1.5 text-sm font-medium transition-colors py-2 px-1"
                style={{ color: PRIMARY }}
              >
                <Plus className="h-4 w-4" />
                {addNewLabel || `New ${label}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
